import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Waypoints, Lock, Mail, Loader2, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useBranding } from "@/context/BrandingContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { formatApiError } from "@/lib/api";
import { BUILD_INFO } from "@/buildInfo";

const BG = "https://images.pexels.com/photos/17194840/pexels-photo-17194840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, login, isMaster } = useAuth();
  const { t } = useI18n();
  const { branding } = useBranding();
  const accent = branding.brand_accent;
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success(t("Zalogowano pomyślnie"));
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Błąd logowania"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-screen w-screen flex relative overflow-hidden" style={branding.brand_bg ? { backgroundColor: branding.brand_bg } : undefined}>
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-cyan-500/15 blur-[120px] kv-float" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-sky-500/10 blur-[120px] kv-float" style={{ animationDelay: "1.5s" }} />
      <div className="pointer-events-none absolute inset-0 kv-grid-bg opacity-50" />
      <div className="hidden lg:block relative flex-1 overflow-hidden">
        <img src={BG} alt="fiber" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F172A] via-[#0F172A]/70 to-transparent" />
        <div className="absolute bottom-14 left-14 max-w-lg">
          <h1 className="font-heading font-black text-5xl text-white tracking-tight leading-tight">
            {branding.brand_name}<span className="text-cyan-400" style={accent ? { color: accent } : undefined}>.</span>
          </h1>
          <p className="text-slate-300 mt-4 text-lg leading-relaxed">
            {t("Profesjonalny system paszportyzacji sieci światłowodowych FTTH — mapa GIS, śledzenie sygnału, mufy, spawy, splittery i klienci w jednym miejscu.")}
          </p>
        </div>
      </div>

      <div className="w-full lg:w-[440px] flex items-center justify-center p-8">
        <div className="w-full max-w-sm kv-glass kv-anim-in rounded-2xl p-7">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-blue-600 flex items-center justify-center overflow-hidden" style={accent ? { backgroundColor: accent } : undefined}>
                {branding.logo_data_url
                  ? <img src={branding.logo_data_url} alt="logo" className="w-full h-full object-contain" data-testid="login-brand-logo" />
                  : <Waypoints className="text-white" size={24} />}
              </div>
              <span className="font-heading font-black text-2xl tracking-tight">{branding.brand_name}</span>
            </div>
          </div>

          <div className="mb-6">
            <div className="text-xs text-slate-500 mb-1.5">{t("Język")}</div>
            <LanguageSwitcher variant="row" />
          </div>

          <h2 className="font-heading font-bold text-xl mb-1">{t("Zaloguj się")}</h2>
          <p className="text-slate-400 text-sm mb-6">{t("Wprowadź dane swojego konta")}</p>

          {isMaster && (
            <div data-testid="login-master-badge"
              className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 px-2.5 py-1 text-[11px] font-medium text-cyan-300">
              <Shield size={12} /> {t("tryb MASTER — wydawca licencji")}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">{t("Email")}</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-3 text-slate-500" />
                <input
                  data-testid="login-email-input"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="admin@example.com" required
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">{t("Hasło")}</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-3 text-slate-500" />
                <input
                  data-testid="login-password-input"
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="••••••••" required
                />
              </div>
            </div>
            <button
              data-testid="login-submit-button" type="submit" disabled={busy}
              style={accent ? { backgroundColor: accent } : undefined}
              className="w-full kv-primary transition-colors text-slate-950 font-semibold rounded-lg py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-cyan-500/25"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {t("Zaloguj się")}
            </button>
          </form>
          <p className="text-slate-500 text-xs mt-6 text-center">
            {t("System paszportyzacji sieci FTTH")} · v{BUILD_INFO.version} · Piotr Niemiec Fiber 1
          </p>
        </div>
      </div>
    </div>
  );
}
